import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';

import IdsDropdown from '../ids-dropdown/ids-dropdown';
import { IdsDropdownOptions } from '../ids-dropdown/ids-dropdown-common';

import '../ids-tag/ids-tag';
import '../ids-text/ids-text';
import '../ids-list-box/ids-list-box-option';
import '../ids-search-field/ids-search-field';

import styles from './ids-multiselect.scss';

import type IdsListBoxOption from '../ids-list-box/ids-list-box-option';
import type IdsTag from '../ids-tag/ids-tag';
import type IdsText from '../ids-text/ids-text';
import IdsCheckbox from '../ids-checkbox/ids-checkbox';
import { escapeHTML } from '../../utils/ids-xss-utils/ids-xss-utils';

/**
 * IDS Multiselect Component
 * @type {IdsMultiselect}
 * @inherits IdsDropdown
 * @part container - the container element
 */
@customElement('ids-multiselect')
@scss(styles)
class IdsMultiselect extends IdsDropdown {
  constructor() {
    super();
  }

  /**
   * Invoked each time the custom element is add into a document-connected element
   */
  connectedCallback() {
    super.connectedCallback();
    this.resetDirtyTracker();
    this.#populateSelected();

    this.listen(['Enter', 'Space', ' '], this, (e: any) => {
      e.preventDefault();
      this.#handleClickEvent(e);
    });

    if (!this.typeahead) {
      const innerInput = this.shadowRoot?.querySelector('ids-trigger-field')?.shadowRoot?.querySelector('input');
      innerInput?.style.setProperty('color', 'transparent');
    }

    if (this.typeahead) {
      this.setOptionsData();

      if (this.tags) {
        this.#handleSearchField(true);
      }
    }

    this.mountMutationObservables();
  }

  disconnectedCallback(): void {
    if (this.mo) {
      this.mo?.disconnect();
      this.mo = undefined;
    }
  }

  /**
   * Watches for changes
   * @property {MutationObserver} mo this button component's mutation observer
   */
  private mo?: MutationObserver;

  optionsData: IdsDropdownOptions = [];

  internalSelectedList: Array<string> = [];

  private initialSelectedList: Array<string> = [];

  /**
   * Return the attributes we handle as getters and setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.TAGS,
      attributes.TAG_COLOR,
      attributes.MAX,
      attributes.SELECT_ALL,
      attributes.SHOW_APPLY,
      attributes.SHOW_CANCEL,
      attributes.SHOW_CLEAR
    ];
  }

  /**
   * Sets the disabled attribute
   * @param {boolean | string} value string value from the disabled attribute
   */
  set disabled(value: boolean | string) {
    if (this.tags) {
      this.input?.querySelectorAll<IdsTag>('ids-tag')?.forEach((element) => {
        element.setAttribute(attributes.DISABLED, 'true');
      });
    }

    super.disabled = value;
  }

  /**
   * Gets the value of the disabled property
   * @returns {boolean} a boolean indicating the elements disabled status
   */
  get disabled(): boolean | string {
    return super.disabled;
  }

  /**
   * Sets the tags attribute which determines if selected options are displayed as tags
   * @param {boolean | string} value string or boolean that sets the use of tag attribute
   */
  set tags(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe !== this.tags) {
      if (valueSafe) {
        this.setAttribute(attributes.TAGS, 'true');
      } else {
        this.offEvent('beforetagremove.multiselect-tag');
        this.setAttribute(attributes.TAGS, 'false');
      }
    }
  }

  /**
   * returns whether the multiselect is set to display selections as tags
   * @returns {boolean | string} a boolean indicating tags setting
   */
  get tags(): boolean | string {
    return stringToBool(this.getAttribute(attributes.TAGS));
  }

  /**
   * Sets the placeholder attribute
   * @param {string} value - the placeholder text
   */
  set placeholder(value: string) {
    const sanitizedValue = this.xssSanitize(value) as string;
    if (value) {
      this.setAttribute(attributes.PLACEHOLDER, sanitizedValue);
      this.input?.setAttribute(attributes.PLACEHOLDER, sanitizedValue);
    } else {
      this.removeAttribute(attributes.PLACEHOLDER);
      this.input?.removeAttribute(attributes.PLACEHOLDER);
    }
  }

  /**
   * Get the placeholder attribute
   * @returns {string} the placeholder text
   */
  get placeholder(): string {
    return this.getAttribute(attributes.PLACEHOLDER) ?? '';
  }

  /**
   * sets the max possible selections for multiselect
   * @param {string|number} value the max number of selections allowed
   */
  set max(value: any) {
    const valueSafe = stringToNumber(value);
    if (valueSafe !== this.max) {
      if (valueSafe) {
        this.setAttribute(attributes.MAX, value);
      } else {
        this.removeAttribute(attributes.MAX);
      }
    }
  }

  /**
   * returns the maximum number of allowed selections
   * @returns {number} the maximum number of selectable options
   */
  get max(): number {
    return stringToNumber(this.getAttribute(attributes.MAX));
  }

  /**
   * Sets the select-all attribute which enables a select all option
   * @param {boolean | string} value string or boolean that enables select all
   */
  set selectAll(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe) {
      this.setAttribute(attributes.SELECT_ALL, '');
    } else {
      this.removeAttribute(attributes.SELECT_ALL);
    }
  }

  /**
   * returns whether the multiselect has select all enabled
   * @returns {boolean | string} a boolean indicating select all setting
   */
  get selectAll(): boolean | string {
    return this.hasAttribute(attributes.SELECT_ALL);
  }

  /**
   * Sets the tag-color attribute which determines the color of tags
   * @param {string} value the color value for tags
   */
  set tagColor(value: string) {
    if (value) {
      this.setAttribute(attributes.TAG_COLOR, value);
    } else {
      this.removeAttribute(attributes.TAG_COLOR);
    }
  }

  /**
   * Gets the tag color
   * @returns {string} the tag color value
   */
  get tagColor(): string {
    return this.getAttribute(attributes.TAG_COLOR) || '';
  }

  /**
   * Sets the show-apply attribute
   * @param {boolean | string} value string or boolean that shows apply button
   */
  set showApply(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe) {
      this.setAttribute(attributes.SHOW_APPLY, 'true');
    } else {
      this.removeAttribute(attributes.SHOW_APPLY);
    }
  }

  /**
   * Gets whether the multiselect shows apply button
   * @returns {boolean} a boolean indicating show apply setting
   */
  get showApply(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_APPLY));
  }

  /**
   * Sets the show-cancel attribute
   * @param {boolean | string} value string or boolean that shows cancel button
   */
  set showCancel(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe) {
      this.setAttribute(attributes.SHOW_CANCEL, 'true');
    } else {
      this.removeAttribute(attributes.SHOW_CANCEL);
    }
  }

  /**
   * Gets whether the multiselect shows cancel button
   * @returns {boolean} a boolean indicating show cancel setting
   */
  get showCancel(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CANCEL));
  }

  /**
   * Sets the show-clear attribute
   * @param {boolean | string} value string or boolean that shows clear button
   */
  set showClear(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe) {
      this.setAttribute(attributes.SHOW_CLEAR, 'true');
    } else {
      this.removeAttribute(attributes.SHOW_CLEAR);
    }
  }

  /**
   * Gets whether the multiselect shows clear button
   * @returns {boolean} a boolean indicating show clear setting
   */
  get showClear(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_CLEAR));
  }

  /**
   * Set the selected values of the multiselect using the provided array
   * @param {Array} value the array of values to set as selected
   */
  set value(value: any) {
    let matched = true;
    value = !Array.isArray(value) ? [String(value ?? '')] : value;
    if (!Array.isArray(value) || value.length > this.max) {
      return;
    }
    value.forEach((selectedValue: string) => {
      const valueSelector = this.generateSelectorForListBoxOption(selectedValue);
      const existingOption = this.dropdownList?.listBox?.querySelector(`${valueSelector}:not([value=""])`);
      if (!existingOption) {
        matched = false;
      }
    });
    if (!matched) {
      return;
    }
    this.internalSelectedList = value;

    (this.container as HTMLInputElement).value = '';
    this.setAttribute(attributes.VALUE, this.internalSelectedList.join(','));
    this.#updateDisplay();
    this.#updateList();
    this.internalSelectedList = value;

    this.container?.classList.toggle('has-value', value.length > 0);
  }

  /**
   * Gets the current value of the component.
   * @returns {Array|string} Returns the internal selected list if it exists and has items,
   * otherwise returns the value attribute from the element.
   * @property {Array|string} value - The current selected value(s).
   */
  get value() {
    return this.internalSelectedList?.length > 0
      ? this.internalSelectedList
      : this.getAttribute(attributes.VALUE);
  }

  /**
   * Returns the selected Listbox option based on the Dropdown's value.
   * @returns {IdsListBoxOption[]} the selected option
   */
  get selectedOptions(): IdsListBoxOption[] {
    let values: string[] = [];

    if (Array.isArray(this.value) && this.value?.length > 0) {
      values = this.value;
    } else if (this.value && this.value.includes(',')) {
      values = this.value?.split?.(',');
    } else {
      values = [this.value];
    }

    const selectors = values.map((value) => `${this.generateSelectorForListBoxOption(value)}:not([group-label="true"])`).join(', ');
    if (!selectors) return [];
    return [...(this.dropdownList?.listBox?.querySelectorAll<IdsListBoxOption>(selectors) ?? [])];
  }

  /**
   * Sets up a MutationObserver to watch for changes in the list box children.
   * When child elements are added or removed, triggers repopulation of selected items.
   * @private
   * @returns {void}
   */
  private mountMutationObservables(): void {
    this.mo = new MutationObserver(() => {
      if (this.typeaheadInput) {
        return;
      }

      this.#populateSelected(true);
    });

    const listBox = this.querySelector('ids-list-box');

    if (listBox) {
      this.mo.observe(listBox, {
        childList: true,
      });
    }
  }

  /**
   * Push label-position to the container element
   * @returns {void}
   */
  onLabelPositionChange(): void {
    if (this.input) this.input.labelPosition = this.labelPosition;
  }

  /**
   * Push label-width to the container element
   * @returns {void}
   */
  onLabelWidthChange(): void {
    if (this.input) this.input.labelWidth = this.labelWidth;
  }

  /**
   * Push label-align-y to the container element
   * @returns {void}
   */
  onLabelAlignYChange(): void {
    if (this.input) this.input.labelAlignY = this.labelAlignY;
  }

  /**
   * Update selected options
   */
  updateSelected() {
    this.#populateSelected();
  }

  /**
   * Rewriting dropdown click events
   */
  attachClickEvent() {
    this.offEvent('click.multiselect-list-box');
    if (this.dropdownList) {
      this.onEvent('click.multiselect-list-box', this.dropdownList.listBox, (e: any) => {
        e.preventDefault();
        this.#handleClickEvent(e);
      });
    }

    this.offEvent('click.multiselect-input');
    if (!this.list) {
      this.onEvent('click.multiselect-input', this.input?.fieldContainer, (e: MouseEvent) => {
        e.stopPropagation();
        // Don't open/close popup on tag removal
        const target = (e.target as IdsTag);
        if (!target?.closest('ids-tag')) {
          this.labelClicked = false;
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.toggle(true);
        }
      });
    }

    if (this.tags) {
      this.offEvent('beforetagremove.multiselect-tag');
      this.onEvent('beforetagremove.multiselect-tag', this.input?.fieldContainer, (e: CustomEvent) => {
        this.#handleTagRemove(e);
      });
    }
  }

  /**
   * Toggles the visibility of the multiselect dropdown
   * @param {boolean} [isOpen] - Optional parameter to force open (true) or closed (false) state
   *                            If not provided, toggles based on current state
   * @returns {void}
   */
  async toggle(isOpen?: boolean) {
    // Don't open the dropdown if the component is disabled
    if (this.disabled) {
      return;
    }

    // Use IdsDropdown's toggle when typeahead is enabled
    if (this.typeahead) {
      // preset class and attributes for current typeahead options
      this.dropdownList?.listBox?.optionsSorted.forEach((item) => {
        item.classList.add('multiselect-option');
        const checkbox = item.querySelector<IdsCheckbox>('ids-checkbox');
        checkbox?.classList.add('justify-center', 'multiselect-checkbox', 'ellipsis');
        checkbox?.setAttribute(attributes.NO_MARGINS, '');
      });
      this.closeOtherFilters();

      super.toggle(isOpen);
      return;
    }

    // Otherwise use our custom multiselect toggle logic
    if (this.popup?.visible) {
      this.close();
      return;
    }

    if (isOpen || (!this.popup?.visible && isOpen !== false)) {
      // Store initial state for cancel functionality
      this.initialSelectedList = [...this.internalSelectedList];

      this.closeOtherFilters();

      // Call typeahead
      if (typeof this.state.beforeShow === 'function') {
        const hasSelectedValues = this.internalSelectedList.length > 0;
        const data = await this.state.beforeShow({
          typeahead: ''
        });
        if (data) {
          this.loadDataSet(data);
          if (this.typeahead) {
            this.optionsData = data;
          }

          this.#populateSelected(hasSelectedValues);
        }
      }

      if (this.popup && this.input) {
        this.dropdownList?.configurePopupPlacement();
        this.popup.visible = true;
        this.input.active = true;

        if (this.autoSize && this.dropdownList?.listBox?.options) {
          this.setWidthBasedOnListBoxOptions(this.dropdownList?.listBox?.options);

          this.dropdownList.dropdownResizeObserver.observe(this.dropdownList);
          const fieldContainer = this.getInputFieldContainer();
          if (fieldContainer) {
            this.dropdownList.dropdownResizeObserver.observe(fieldContainer);
            this.popup?.style.setProperty('max-width', `${fieldContainer.getBoundingClientRect().width}px`);
          }
        }
      }
      this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');
      this.container?.classList.add('is-open');
      if (this.dropdownList?.listBox) {
        this.dropdownList.listBox.setAttribute(htmlAttributes.TABINDEX, '0');
      }

      this.focus();
      if (this.typeahead && this.tags) {
        this.#handleSearchField(true);
      }
      this.#updateList();
    }
  }

  /**
   * Restore the initial selected state (used by cancel functionality)
   */
  restoreInitialState(): void {
    this.internalSelectedList = [...this.initialSelectedList];
    this.value = this.internalSelectedList;
    this.#updateDisplay();
    this.#updateList();
  }

  /**
   * Establish selection event for keyboard interactions.
   * Overrides a similiar method from IdsDropdown for Multiselect-specific behavior.
   */
  attachKeyboardSelectionEvent() {
    // Select or Open on space/enter
    this.listen([' ', 'Enter'], this, () => {
      if (!this.dropdownList?.popup?.visible) return;
      if (this.openedByKeyboard) {
        this.openedByKeyboard = false;
        return;
      }

      if (this.selected) {
        this.#optionChecked(this.selected);
      }
    });
  }

  /**
   * Removes selected attributes from an option
   * @param {HTMLElement} option element to remove attributes
   */
  deselectOption(option: HTMLElement | undefined | null) {
    super.deselectOption(option);
    this.removeAttribute(attributes.VALUE);
  }

  /**
   * Closes all other open multiselect and dropdown filters to ensure only one filter is open at a time.
   * Searches both regular DOM and data grid shadow DOM for open filters.
   * @returns {void}
   */
  closeOtherFilters() {
    document.querySelectorAll('ids-multiselect').forEach((el: any) => {
      if (el !== this && el.container?.classList.contains('is-open')) el.close();
    });
    if (!this.classList.contains('data-grid-filter')) {
      return;
    }

    const openElements = document.querySelectorAll('ids-multiselect[aria-expanded="true"], ids-dropdown[aria-expanded="true"]');
    openElements.forEach((element) => {
      if (element !== this) {
        if (element.tagName.toLowerCase() === 'ids-dropdown') {
          (element as IdsDropdown).close();
        } else {
          (element as IdsMultiselect).close();
        }
      }
    });

    const shadowElements = document.querySelectorAll('ids-data-grid');
    shadowElements.forEach((grid) => {
      const shadowMultiselects = grid.shadowRoot?.querySelectorAll('ids-multiselect');
      shadowMultiselects?.forEach((element) => {
        if (element !== this && (element as any).container?.classList.contains('is-open')) {
          (element as any).close();
        }
      });
    });
  }

  /**
   * Close the dropdown popup
   * Rewriting it to add multiselect value update
   * @param {boolean} noFocus if true do not focus on close
   */
  close(noFocus?: boolean) {
    if (this.popup && this.input) {
      this.popup.visible = false;
      this.input.active = false;
      this.removeFieldContainerStyles();
      this.dropdownList?.dropdownResizeObserver.disconnect();
    }

    this.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');
    this.dropdownList?.listBox?.removeAttribute('tabindex');

    if (this.selected) {
      this.deselectOption(this.selected);
    }

    if (this.typeahead) {
      // In case unfinished typeahead (typing is in process)
      // closing popup will reset multiselect to the initial value
      this.input?.setAttribute(attributes.READONLY, 'true');
      this.#updateDisplay();
      this.loadDataSet(this.optionsData);
      (window.getSelection() as Selection).removeAllRanges();
      this.searchField?.clear();
      this.replaceTriggerIcon(this.dropdownIcon || 'dropdown');
    }

    if (!noFocus) {
      this.input?.focus();
    }

    this.typeaheadInput = null;
    this.value = this.internalSelectedList;

    this.container?.classList.remove('is-open');

    this.triggerEvent('close', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });
  }

  /**
   * Triggers when dismissible tag is removed
   * @param {CustomEvent} e click event
   */
  #handleTagRemove(e: CustomEvent) {
    this.value = this.internalSelectedList.filter((value: string) => value !== (e.target as IdsMultiselect)?.dataset.value);
    this.popup?.place();
  }

  /**
   * Check option checkbox and update selected list
   * @param {IdsListBoxOption} option selected ids-list-box-option element
   */
  #optionChecked(option: IdsListBoxOption) {
    if (!option || option?.hasAttribute(attributes.GROUP_LABEL)) return;

    const value = option.getAttribute('value');

    // Handle select all option
    if (value === 'select-all') {
      this.handleSelectAll(option);
      return;
    }

    const isSelected = this.internalSelectedList.some((item) => value === item);
    const checkbox = option.querySelector<IdsCheckbox>('ids-checkbox');
    const canSelect = this.max !== this.value?.length;

    if (isSelected || canSelect) {
      this.internalSelectedList = isSelected
        ? this.internalSelectedList.filter((item) => item !== value)
        : [...this.internalSelectedList, value].filter((item): item is string => item !== null);
      if (!this.internalSelectedList.length) this.setAttribute('value', '');

      if (checkbox) {
        checkbox.onEvent('change', checkbox, (e: CustomEvent) => {
          e.stopPropagation();
        });
        checkbox.checked = !isSelected;
        checkbox.offEvent('change', checkbox);
      }

      this.#updateDisplay();
      this.updateSelectAllState();
      this.popup?.place();
      this.container?.classList.toggle('has-value', this.internalSelectedList.length > 0);

      this.triggerEvent('selected', this, {
        bubbles: true,
        detail: {
          value,
          label: option.textContent?.trim() || '',
          selectedOption: option,
          selectedOptions: this.selectedOptions,
          action: isSelected ? 'deselected' : 'selected'
        }
      });
    }

    this.clearSelected();
    this.selectOption(option);
  }

  /**
   * Add custom placeholder text to the input
   */
  #addPlaceholder() {
    this.input?.setAttribute(attributes.PLACEHOLDER, this.placeholder);
  }

  /**
   * Remove placeholder text from the input
   */
  #removePlaceholder() {
    this.input?.querySelector<IdsText>('.placeholder')?.remove();
  }

  /**
   * Update value in the input visually
   */
  #updateDisplay() {
    let selected: IdsListBoxOption[] = [];

    if (this.internalSelectedList.length > 0) {
      const optionsSorted = this.dropdownList?.listBox?.optionsSorted ?? [];
      selected = optionsSorted.filter((item: IdsListBoxOption) => this.internalSelectedList.includes(item.value || ''));
    } else {
      selected = this.selectedOptions;
    }

    const newValue = selected.map((item: IdsListBoxOption) => item.label).join(', ');

    // Clear tags/text and placeholder before rerender
    this.input?.querySelectorAll<IdsTag>('ids-tag').forEach((item) => item.remove());
    this.input?.querySelector<IdsText>('ids-text')?.remove();
    this.#removePlaceholder();

    if (this.placeholder && selected.length === 0) {
      this.#addPlaceholder();
    }

    if (this.tags) {
      const tags = selected.map((item: any) => {
        const disabled = this.disabled ? ` disabled="true"` : ``;

        return `<ids-tag
          class="multiselect-tag"
          data-value="${item.value}"
          ${this.tagColor ? `color="${this.tagColor}"` : ''}
          dismissible="true"
          text-ellipsis
          ${disabled}
        >
          <ids-text overflow="ellipsis" tooltip="true">${item.label}</ids-text>
        </ids-tag>`;
      }).join('');
      this.input?.insertAdjacentHTML('afterbegin', tags);
    } else {
      if (!this.typeahead) {
        this.input?.insertAdjacentHTML('afterbegin', `<ids-text overflow="ellipsis" tooltip="true">${escapeHTML(newValue)}</ids-text>`);
      }

      const text = this.input?.querySelector<IdsText>('ids-text');
      const fieldContainer = this.input?.fieldContainer;

      // Adjust settings for the tooltip
      if (text?.beforeTooltipShow) {
        text.beforeTooltipShow = (tooltip) => {
          tooltip.popup.align = 'top, left';
          tooltip.popup.alignTarget = fieldContainer;
          tooltip.target = fieldContainer;
          tooltip.popup.style?.setProperty('text-align', 'center');

          if (fieldContainer?.clientWidth) {
            tooltip.popup.width = `${fieldContainer.clientWidth - 14}px`;
          }
        };
      }
    }

    if (this.input?.input) {
      const beforeValue = this.input.value;
      this.input.value = newValue;
      this.input.input.value = newValue;

      if (!this.typeahead) {
        return;
      }

      // For typeahead mode, set tooltip on the input field itself
      this.input.setAttribute(attributes.TOOLTIP, newValue);

      // Add centered text styling for typeahead tooltip
      if (this.input.beforeTooltipShow) {
        this.input.beforeTooltipShow = (tooltip) => {
          tooltip.popup.style?.setProperty('text-align', 'center');
        };
      }

      // explicitly trigger the change event when user typeahead exact option text and selects it
      if (beforeValue === newValue) {
        this.triggerEvent('change', this, { bubbles: true });
      }
    }
  }

  /**
   * Render dropdown list with selected options on top
   */
  #updateList() {
    const listBox = this.dropdownList?.listBox;
    if (!listBox) return;

    this.dropdownList?.syncRowIndex();
    const selectedList = this.internalSelectedList;
    const selectedOptions: IdsListBoxOption[] = [];
    const unselectedOptions: IdsListBoxOption[] = [];
    const selectAllOptions: IdsListBoxOption[] = [];

    listBox?.shadowRoot?.querySelector('.first-selected')?.remove();

    listBox?.querySelectorAll('.multiselect-separator')?.forEach((elem) => {
      elem?.remove();
    });

    // Add select all option if enabled
    if (this.selectAll) {
      this.addSelectAllOption();
    }

    (listBox?.optionsSorted ?? [])
      .forEach((option: IdsListBoxOption) => {
        option.classList.remove('last-selected');
        option.hidden = false;
        if (option?.childCheckbox) option.childCheckbox.offEvent('change');

        const value = option.getAttribute(attributes.VALUE);
        if (value === 'select-all') {
          selectAllOptions.push(option);
        } else if (selectedList.includes(value || '')) {
          option.selected = true;
          selectedOptions.push(option);
        } else {
          option.selected = false;
          unselectedOptions.push(option);
        }
      });

    selectedOptions.at(-1)?.classList.add('last-selected');

    if (selectedOptions.length > 0 && unselectedOptions.length > 0) {
      const separator = document.createElement('ids-separator');
      separator.classList.add('multiselect-separator');
      selectedOptions.push(separator as any);
    }

    // Arrange options: select-all first, then selected, then unselected
    const orderedOptions = [...selectAllOptions, ...selectedOptions, ...unselectedOptions];
    listBox?.prepend(...orderedOptions);

    listBox?.querySelectorAll('.first-selected')?.forEach((elem) => {
      elem?.remove();
    });

    const selectedText = '<ids-text class="first-selected" color="foreground-default-tertiary" font-weight="semi-bold" translate-text="true">Selected</ids-text>';
    if (selectedOptions && selectedOptions.length > 0 && this.selectAll) {
      const selectAllOption = listBox?.querySelector('ids-list-box-option[value="select-all"]');
      if (selectAllOption) {
        selectAllOption.insertAdjacentHTML('afterend', selectedText);
      }
    } else if (selectedOptions && selectedOptions.length > 0) {
      listBox?.container?.insertAdjacentHTML('beforebegin', selectedText);
    }

    this.updateSelectAllState();
  }

  /**
   * Add select all option to the list box
   */
  private addSelectAllOption() {
    const listBox = this.dropdownList?.listBox;
    if (!listBox || listBox.querySelector('ids-list-box-option[value="select-all"]')) return;

    const existingSelectAll = listBox.querySelector('ids-list-box-option[value="select-all"]');
    if (existingSelectAll) {
      existingSelectAll.remove();
    }

    const selectAllOption = document.createElement('ids-list-box-option');
    selectAllOption.setAttribute(attributes.VALUE, 'select-all');
    selectAllOption.innerHTML = `
      <ids-checkbox no-margin class="justify-center multiselect-checkbox ellipsis" label="Select all"></ids-checkbox>
    `;
    selectAllOption.classList.add('multiselect-option', 'select-all-option');

    listBox.insertAdjacentElement('afterbegin', selectAllOption);
  }

  /**
   * Populates the internal selected list by syncing checkbox states with selected options.
   * Updates checkboxes, applies styling, and optionally updates the component value.
   * @private
   * @param {boolean} [skipValueUpdate] - Whether to skip updating the component's value
   * @returns {void}
   */
  #populateSelected(skipValueUpdate = false) {
    if (!skipValueUpdate) {
      this.internalSelectedList = [];
    }

    const optionsSorted = this.dropdownList?.listBox?.optionsSorted ?? [];

    this.selectedOptions.forEach((item: IdsListBoxOption) => {
      const checkbox = item.querySelector<IdsCheckbox>('ids-checkbox');
      if (checkbox) checkbox.checked = true;
      item.setAttribute(attributes.SELECTED, '');
    });

    optionsSorted?.forEach((item: IdsListBoxOption) => {
      const checkbox = item.querySelector<IdsCheckbox>('ids-checkbox');
      const value = item.getAttribute('value');

      // Skip select-all option for internal selected list
      if (value === 'select-all') {
        if (this.size) {
          checkbox?.setAttribute(attributes.SIZE, this.size);
        }
        checkbox?.setAttribute('no-margin', '');
        checkbox?.classList.add('justify-center', 'multiselect-checkbox', 'ellipsis');
        item.classList.add('multiselect-option');
        return;
      }

      if (checkbox?.checked) item.setAttribute(attributes.SELECTED, '');

      if (!skipValueUpdate && item.hasAttribute(attributes.SELECTED)) {
        if (value) {
          this.internalSelectedList.push(value);
        }

        if (checkbox && !checkbox?.checked) {
          checkbox.checked = true;
        }
      }

      if (this.size) {
        checkbox?.setAttribute(attributes.SIZE, this.size);
      }

      checkbox?.setAttribute('no-margin', '');
      checkbox?.classList.add('justify-center', 'multiselect-checkbox', 'ellipsis');
      item.classList.add('multiselect-option');
    });

    if (skipValueUpdate) {
      return;
    }

    this.value = this.internalSelectedList;
    this.updateSelectAllState();
  }

  /**
   * Set typeahead attribute
   * @param {boolean | string} value typeahead value
   */
  set typeahead(value: boolean | string) {
    const val = stringToBool(value);
    const innerInput = this.shadowRoot?.querySelector('ids-trigger-field')?.shadowRoot?.querySelector('input');

    if (val) {
      this.setAttribute(attributes.TYPEAHEAD, String(val));
      this.setOptionsData();
      innerInput?.style.removeProperty('color');
      if (this.tags) {
        this.#handleSearchField(true);
      }
    } else {
      innerInput?.style.setProperty('color', 'transparent');
      this.#handleSearchField(false);
    }
    this.#updateDisplay();

    this.container?.classList.toggle('typeahead', val);
  }

  /**
   * Get the typeahead attribute
   * @returns {boolean} typeahead attribute value converted to boolean
   */
  get typeahead(): boolean {
    return stringToBool(this.getAttribute(attributes.TYPEAHEAD));
  }

  #handleSearchField(shouldAdd?: boolean) {
    requestAnimationFrame(() => {
      this.searchField?.remove();
      if (shouldAdd) {
        this.popup?.insertAdjacentHTML('afterbegin', `<ids-search-field slot="content" label-state="collapsed" placeholder="" color-variant="dropdown" no-margins></ids-search-field>`);
      }
    });
  }

  /**
   * Handle select all checkbox toggle
   * @param {IdsListBoxOption} selectAllOption the select all option element
   */
  private handleSelectAll(selectAllOption: IdsListBoxOption) {
    const checkbox = selectAllOption.querySelector<IdsCheckbox>('ids-checkbox');
    const allOptions = this.getAllSelectableOptions();
    const allValues = allOptions.map((option) => option.getAttribute('value')).filter((value): value is string => value !== null);
    const isSelectingAll = !checkbox?.checked;

    if (isSelectingAll) {
      this.internalSelectedList = [...allValues];
    } else {
      this.internalSelectedList = [];
    }

    // Update all option checkboxes
    allOptions.forEach((option) => {
      if (option.hasAttribute(attributes.DISABLED)) return;

      const optionCheckbox = option.querySelector<IdsCheckbox>('ids-checkbox');
      if (optionCheckbox) {
        optionCheckbox.checked = isSelectingAll;
      }
    });

    // Update select all checkbox
    if (checkbox) {
      checkbox.checked = isSelectingAll;
    }

    this.#updateDisplay();
    this.popup?.place();
    this.container?.classList.toggle('has-value', this.internalSelectedList.length > 0);

    this.triggerEvent('selected', this, {
      bubbles: true,
      detail: {
        value: 'select-all',
        label: 'Select all',
        selectedOption: selectAllOption,
        selectedOptions: this.selectedOptions,
        action: isSelectingAll ? 'selected-all' : 'deselected-all'
      }
    });
  }

  /**
   * Get all selectable options (excluding select-all and group labels)
   * @returns {IdsListBoxOption[]} array of selectable options
   */
  private getAllSelectableOptions(): IdsListBoxOption[] {
    const listBox = this.dropdownList?.listBox;
    if (!listBox) return [];

    return [...listBox.querySelectorAll<IdsListBoxOption>('ids-list-box-option')]
      .filter((option) => !option.hasAttribute(attributes.GROUP_LABEL)
        && option.getAttribute(attributes.VALUE) !== 'select-all');
  }

  /**
   * Update select all checkbox state based on current selections
   */
  updateSelectAllState() {
    if (!this.selectAll) return;

    const selectAllOption = this.dropdownList?.listBox?.querySelector<IdsListBoxOption>('ids-list-box-option[value="select-all"]');
    const selectAllCheckbox = selectAllOption?.querySelector<IdsCheckbox>('ids-checkbox');

    if (selectAllCheckbox) {
      const allOptions = this.getAllSelectableOptions();
      const allValues = allOptions
        .map((option) => option.getAttribute(attributes.VALUE))
        .filter((value): value is string => value !== null);
      const allSelected = allValues.length === this.internalSelectedList.length;
      selectAllCheckbox.checked = allSelected;
    }
  }

  #handleClickEvent(event: any) {
    const option = event.target.nodeName === 'IDS-LIST-BOX-OPTION'
      || event.target.closest('ids-list-box-option')
      ? event.target.closest('ids-list-box-option') ?? event.target
      : null;

    if (!option) {
      return;
    }

    this.#optionChecked(option);
  }
}

export default IdsMultiselect;
